/**
 * Chord symbol formatting.
 *
 * Everything `formatChord` produces is round-trippable: `parseChordSymbol(formatChord(spec))`
 * is deep-equal to `spec`, for every option combination. That property is what lets the
 * correction UI show a symbol, take an edit, and re-parse without drift.
 *
 * The reverse is not true and is not meant to be — `C7#9` and `C7(♯9)` are the same chord,
 * and the formatter picks one of them.
 */

import { noteName } from './enharmonic.js';
import { EXTENSION_ORDER, type ChordSpec, type Extension } from './types.js';

export interface FormatOptions {
  /** ASCII output only: `b`/`#` instead of `♭`/`♯`, and no `°`/`ø`/`Δ`. Default false. */
  ascii?: boolean;
  /** Default `'m'`. */
  minorSymbol?: 'm' | 'min' | '-';
  /** Default `'maj'`. */
  majorSeventhSymbol?: 'maj' | 'M' | 'Δ';
  /** Default `'dim'`. */
  diminishedSymbol?: 'dim' | '°';
  /** Default `'+'`. */
  augmentedSymbol?: 'aug' | '+';
  /** Default `'m7b5'`, which renders as `m7♭5` unless `ascii`. */
  halfDiminishedSymbol?: 'm7b5' | 'ø';
  /** Set false to render the chord without its slash bass. Default true. */
  includeBass?: boolean;
}

interface ResolvedOptions {
  ascii: boolean;
  minorSymbol: string;
  majorSeventhSymbol: string;
  diminishedSymbol: string;
  augmentedSymbol: string;
  halfDiminishedSymbol: 'm7b5' | 'ø';
  includeBass: boolean;
  flat: string;
  sharp: string;
}

function resolveOptions(opts: FormatOptions | undefined): ResolvedOptions {
  const ascii = opts?.ascii ?? false;
  const majorSeventhSymbol = opts?.majorSeventhSymbol ?? 'maj';
  const diminishedSymbol = opts?.diminishedSymbol ?? 'dim';
  const halfDiminishedSymbol = opts?.halfDiminishedSymbol ?? 'm7b5';
  return {
    ascii,
    minorSymbol: opts?.minorSymbol ?? 'm',
    // `Δ` and `°` and `ø` are not ASCII, so `ascii: true` overrides them.
    majorSeventhSymbol: ascii && majorSeventhSymbol === 'Δ' ? 'maj' : majorSeventhSymbol,
    diminishedSymbol: ascii && diminishedSymbol === '°' ? 'dim' : diminishedSymbol,
    augmentedSymbol: opts?.augmentedSymbol ?? '+',
    halfDiminishedSymbol: ascii && halfDiminishedSymbol === 'ø' ? 'm7b5' : halfDiminishedSymbol,
    includeBass: opts?.includeBass ?? true,
    flat: ascii ? 'b' : '♭',
    sharp: ascii ? '#' : '♯',
  };
}

/** How a leftover extension is written when it is not absorbed into the head numeral. */
function extensionText(extension: Extension, o: ResolvedOptions): string {
  switch (extension) {
    case 'b9':
      return `${o.flat}9`;
    case '#9':
      return `${o.sharp}9`;
    case '#11':
      return `${o.sharp}11`;
    case 'b13':
      return `${o.flat}13`;
    case '6':
      return 'add6';
    case '9':
      return 'add9';
    case '11':
      return 'add11';
    case '13':
      return 'add13';
  }
}

/** Format a structural chord spec as a display symbol. */
export function formatChord(spec: ChordSpec, opts?: FormatOptions): string {
  const o = resolveOptions(opts);
  const root = noteName(spec.root, { ascii: o.ascii });
  const bass = o.includeBass && spec.bass ? `/${noteName(spec.bass, { ascii: o.ascii })}` : '';

  const extensions = new Set(spec.extensions);
  const consumed = new Set<Extension>();

  // A power chord has no third and no stack; everything else becomes an explicit add.
  if (spec.quality === 'power') {
    const tail = buildTail(spec, extensions, consumed, o, false);
    return `${root}5${tail}${bass}`;
  }

  // A diminished seventh on anything other than a diminished triad cannot be written
  // with a plain numeral — `C7` always means a minor seventh. Such chords do not come
  // out of `parseChordSymbol`, but they can be constructed by hand or arrive from the
  // LLM, so they get an explicit marker rather than a silently wrong symbol.
  if (spec.seventh === 'dim7' && spec.quality !== 'diminished') {
    const explicitQuality =
      spec.quality === 'minor'
        ? o.minorSymbol
        : spec.quality === 'augmented'
          ? o.augmentedSymbol
          : spec.quality === 'major'
            ? 'maj'
            : '';
    const sus = spec.quality === 'sus2' || spec.quality === 'sus4' ? spec.quality : '';
    const tail = buildTail(spec, extensions, consumed, o, false);
    return `${root}${explicitQuality}${sus}(dim7)${tail}${bass}`;
  }

  const hasPlainNinth = extensions.has('9');
  const hasAnyNinth = hasPlainNinth || extensions.has('b9') || extensions.has('#9');

  // The head numeral names the tallest *natural* extension the symbol can imply.
  // `13` implies a ninth, so it is only usable when some ninth is present; when that
  // ninth is altered it stays visible in the parenthesised tail (C13♭9).
  let numeral: string | null = null;
  if (spec.seventh) {
    if (extensions.has('13') && hasAnyNinth) {
      numeral = '13';
      consumed.add('13');
      if (hasPlainNinth) consumed.add('9');
    } else if (extensions.has('11') && hasAnyNinth) {
      numeral = '11';
      consumed.add('11');
      if (hasPlainNinth) consumed.add('9');
    } else if (hasPlainNinth) {
      numeral = '9';
      consumed.add('9');
    } else {
      numeral = '7';
    }
  } else if (extensions.has('6')) {
    if (hasPlainNinth) {
      numeral = '6/9';
      consumed.add('6');
      consumed.add('9');
    } else {
      numeral = '6';
      consumed.add('6');
    }
  }

  const n = numeral ?? '';
  const seventhNumeral = numeral ?? '7';
  let head: string;

  switch (spec.quality) {
    case 'major':
      head = spec.seventh === 'maj7' ? `${o.majorSeventhSymbol}${seventhNumeral}` : n;
      break;
    case 'minor':
      head =
        spec.seventh === 'maj7'
          ? `${o.minorSymbol}(${o.majorSeventhSymbol}${seventhNumeral})`
          : `${o.minorSymbol}${n}`;
      break;
    case 'diminished':
      if (spec.seventh === 'dim7') {
        head = `${o.diminishedSymbol}${seventhNumeral}`;
      } else if (spec.seventh === 'min7') {
        head =
          o.halfDiminishedSymbol === 'ø'
            ? `ø${seventhNumeral}`
            : `${o.minorSymbol}${seventhNumeral}${o.flat}5`;
      } else if (spec.seventh === 'maj7') {
        head = `${o.diminishedSymbol}(${o.majorSeventhSymbol}${seventhNumeral})`;
      } else {
        head = `${o.diminishedSymbol}${n}`;
      }
      break;
    case 'augmented':
      if (spec.seventh === 'min7') {
        head = `${seventhNumeral}${o.sharp}5`;
      } else if (spec.seventh === 'maj7') {
        head = `${o.majorSeventhSymbol}${seventhNumeral}${o.sharp}5`;
      } else {
        head = `${o.augmentedSymbol}${n}`;
      }
      break;
    case 'sus2':
    case 'sus4': {
      const sus = spec.quality;
      head =
        spec.seventh === 'maj7' ? `${o.majorSeventhSymbol}${seventhNumeral}${sus}` : `${n}${sus}`;
      break;
    }
  }

  const tail = buildTail(spec, extensions, consumed, o, numeral !== null);
  return `${root}${head}${tail}${bass}`;
}

/**
 * The alteration suffix plus the parenthesised leftovers.
 * `alterationsInHead` records whether the head already spelled a ♯5 or ♭5.
 */
function buildTail(
  spec: ChordSpec,
  extensions: Set<Extension>,
  consumed: Set<Extension>,
  o: ResolvedOptions,
  hasNumeral: boolean,
): string {
  let alterations = '';
  for (const alteration of spec.alterations) {
    alterations += alteration === 'b5' ? `${o.flat}5` : `${o.sharp}5`;
  }

  const parts: string[] = [];
  for (const extension of EXTENSION_ORDER) {
    if (extensions.has(extension) && !consumed.has(extension)) {
      parts.push(extensionText(extension, o));
    }
  }
  const omissionParts = spec.omissions.map((degree) => `no${degree}`);

  if (parts.length === 0 && omissionParts.length === 0) return alterations;

  const all = [...parts, ...omissionParts];
  // A single `addN` on a bare triad reads better without brackets: Cadd9, not C(add9).
  const bare =
    omissionParts.length === 0 &&
    all.length === 1 &&
    !hasNumeral &&
    alterations.length === 0 &&
    (all[0] as string).startsWith('add');

  return bare ? `${alterations}${all[0]}` : `${alterations}(${all.join(',')})`;
}
